import React, { useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import { Button, Form, message, Input, Select, Switch, InputNumber, Row, Col, AutoComplete } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { routeNames } from "lib/routes";
import { useEventContext } from "ui/components/event/eventContext";
import { getSession } from "lib/utils/utils";
import { useGetUser, useGetEventById, useEditEventById } from "lib/hooks";
import SubmitButton from "ui/components/base/Buttons/SubmitButton";
import { ReactComponent as SelectIcon } from "images/modal/Frameselect.svg";
import { EnvironmentFilled } from "@ant-design/icons";
import { MAPBOX_PUBLIC_KEY } from "lib/constants";
import {
  ButtonContainer,
  ButtonWrapper,
  CardDisabled,
  ComingSoonButton,
  Container,
  EventTypeCard,
  EventTypeCardDisabled,
  EventTypeContainer,
  EventTypeDescription,
  EventTypeLabel,
  GhostButton,
  Left,
  MapWrapper,
  Right,
  SmallerText,
  SmallText,
  StatusLeft,
  StatusRight,
  TicketStatus,
  Title,
} from "./eventtype.style";
import { useAddressAutofillCore } from "@mapbox/search-js-react";
import Map, { Marker, NavigationControl } from "react-map-gl/mapbox";
import { parseLegacyLocationString } from "lib/utils/location-utils";

import "mapbox-gl/dist/mapbox-gl.css";
import { ILocation, RegionOption } from "lib/types";
import { RequiredMark, VisibilityWrapper } from "ui/components/base";
import { countryOptions } from "lib/data/countries";
import TextArea from "antd/es/input/TextArea";

interface LocationSubmitEvent extends React.FormEvent<HTMLFormElement> {}

const MAPBOX_TOKEN = MAPBOX_PUBLIC_KEY;

const EventType: React.FC = () => {
  const { mutate: editEvent } = useEditEventById();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]); // State to store location suggestions
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null); // State to store the selected location
  const [initialLocation, setInitialLocation] = useState<ILocation | null>(null); // State to store previous location
  const [isManualAddress, setIsManualAddress] = useState<boolean>(false);
  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<any>([]);
  const [selectedRegion, setSelectedRegion] = useState<any>([]);

  const [form] = Form.useForm();

  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const mapRef = useRef<any>(null);

  const navigate = useNavigate();
  const autofill = useAddressAutofillCore({ accessToken: MAPBOX_TOKEN, country: "ng" });

  const { eventId: rawEventId } = useEventContext();
  const eventId = rawEventId ? Number(rawEventId) : null;
  const { data: existingEventData } = useGetEventById({ id: Number(rawEventId) });
  const { data: user } = useGetUser();
  const organizerEmail = useMemo(() => {
    const session = getSession();
    return user?.email || session?.email;
  }, [user]);
  const handleCardClick = (type: string) => {
    setSelectedType(prevType => (prevType === type ? null : type));
  };

  const { data: event, error: eventError } = eventId ? useGetEventById({ id: eventId }) : { data: null, error: null };

  // Modified handleLocationSelect to update both state and form value
  const handleLocationSelect = (location: string) => {
    setSelectedLocation(location);
    form.setFieldsValue({ location }); // Update form value
    setSuggestions([]); // Clear suggestions
    console.log("Selected location:", location);
  };

  const handleLocationSubmit = (e: LocationSubmitEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMapMove = () => {
    const center = mapRef.current?.getMap()?.getCenter();
    if (center) {
      form.setFieldValue(["location", "latitude"], center.lat);
      form.setFieldValue(["location", "longitude"], center.lng);
    }
  };

  const handleSubmit = async () => {
    if (!eventId) {
      message.error("Create an event first");
      return;
    }

    if (!selectedType) {
      message.error("Please select an event type.");
      return;
    }

    try {
      const values = await form.validateFields();

      // Parse the selected location
      const locationParts = selectedLocation
        ? parseLegacyLocationString(selectedLocation)
        : {
            address: "",
            city: "",
            state: "",
            country: "",
          };

      const locationString = JSON.stringify(locationParts);

      editEvent(
        {
          id: eventId,
          data: {
            ...existingEventData,
            type: selectedType,
            organizerEmail: organizerEmail,
            location: locationString,
          },
        },
        {
          onSuccess: () => navigate(routeNames.eventDate),
          onError: () => message.error("Failed to update the event."),
        },
      );
    } catch (error) {
      console.error("Validation failed:", error);
      message.error("Please fill in all required fields.");
    }
  };

  useEffect(() => {
    console.log("values", form.getFieldsValue());
  }, []);

  // useEffect(() => {
  //   const location = form.getFieldValue('location');
  //   if (map && marker && location?.latitude && location?.longitude) {
  //     const coords = [location.longitude, location.latitude];
  //     map.setCenter(coords);
  //     marker.setLngLat(coords);
  //   }
  // }, [form, map, marker]);

  return (
    <Container>
      <Title>Select Event Type</Title>
      <EventTypeContainer>
        <EventTypeCard selected={selectedType === "physical"} onClick={() => handleCardClick("physical")}>
          <Left>
            <EventTypeLabel selected={selectedType === "physical"}>Physical event</EventTypeLabel>
            <EventTypeDescription selected={selectedType === "physical"}>
              At a venue or physical location
            </EventTypeDescription>
          </Left>
          <Right visible={selectedType === "physical"}>
            <SelectIcon />
          </Right>
        </EventTypeCard>

        <EventTypeCardDisabled selected={selectedType === "online"} disabled>
          <CardDisabled>
            <EventTypeLabel>Online event</EventTypeLabel>
            <ComingSoonButton>Coming Soon</ComingSoonButton>
          </CardDisabled>
          <EventTypeDescription>Virtual live stream hosting</EventTypeDescription>
        </EventTypeCardDisabled>

        <EventTypeCardDisabled selected={selectedType === "hybrid"} disabled>
          <CardDisabled>
            <EventTypeLabel>Hybrid event</EventTypeLabel>
            <ComingSoonButton>Coming Soon</ComingSoonButton>
          </CardDisabled>

          <EventTypeDescription>Host physically & virtually</EventTypeDescription>
        </EventTypeCardDisabled>
      </EventTypeContainer>
      {selectedType === "physical" && (
        <>
          <AutoComplete
            allowClear
            options={suggestions}
            style={{ width: 500, maxWidth: 500 }}
            popupMatchSelectWidth={500}
          >
            <Input.Search size="large" placeholder="Type a location" />
          </AutoComplete>
          <TicketStatus>
            <StatusLeft>
              <SmallText>Enter Manual Address</SmallText>
              <SmallerText>Can&apos;t find your exact address on the map?</SmallerText>
            </StatusLeft>
            <StatusRight>
              <Switch checked={isManualAddress} onChange={setIsManualAddress} />
            </StatusRight>
          </TicketStatus>
        </>
      )}
      {/* Map */}
      <MapWrapper isVisible={selectedType === "physical"}>
        <Map
          ref={mapRef}
          initialViewState={{
            longitude: initialLocation?.longitude ?? 3.3792,
            latitude: initialLocation?.latitude ?? 6.5244,
            zoom: 10,
          }}
          mapStyle="mapbox://styles/mapbox/streets-v11"
          mapboxAccessToken={MAPBOX_TOKEN}
          onMoveEnd={handleMapMove}
        >
          <NavigationControl position="top-left" />
          {initialLocation?.longitude && initialLocation?.latitude && (
            <Marker longitude={initialLocation.longitude} latitude={initialLocation.latitude} color="red" />
          )}
        </Map>
      </MapWrapper>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        initialValues={{ location: initialLocation || {} }}
        style={{ width: "100%", margin: "0 auto" }}
        requiredMark={RequiredMark}
        disabled={!isManualAddress}
        size="large"
        variant="outlined"
        // ref={formRef as any}
      >
        <VisibilityWrapper isVisible={selectedType === "physical"}>
          <Form.Item
            name={["location", "streetName"]}
            label="Street Name"
            rules={[{ required: true, message: "Street name is required" }]}
          >
            <Input autoComplete="address-line1" name="address-line1" placeholder="eg. 10 Niamey St" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name={["location", "country"]}
                label="Country"
                rules={[{ required: true, message: "Country is required" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select Country"
                  options={countryOptions}
                  onChange={val => form.setFieldValue("adminArea", undefined)}
                  defaultValue={{ label: "Nigeria", value: "NG", flag: "\ud83c\uddf3\ud83c\uddec" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={["location", "adminArea"]} label="State / Province">
                <VisibilityWrapper isVisible={!isManualAddress} fallback={<Select options={regions} />}>
                  <Input autoComplete="address-level1" />
                </VisibilityWrapper>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name={["location", "city"]} label="City" rules={[{ required: true, message: "City is required" }]}>
            <Input autoComplete="address-level2" name="address-level2" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name={["location", "postalCode"]} label="Postal Code">
                <Input autoComplete="postal-code" name="postal-code" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={["location", "unit"]} label="Unit / Apt / Suite">
                <Input autoComplete="address-line2" name="address-line2" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name={["location", "streetPredirection"]} label="Street Direction">
                <Input placeholder=" (e.g. N, S, W, SW)" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={["location", "streetSuffix"]} label="Street Type">
                <Input placeholder="(e.g. St, Ave)" autoComplete="off" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name={["location", "landmark"]} label="Landmark">
                <TextArea autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={["location", "neighborhood"]} label="Neighborhood">
                <TextArea autoComplete="off" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name={["location", "plusCode"]} label="Open Location Code (Plus Code)">
                <Input autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name={["location", "latitude"]} label="Latitude">
                <InputNumber style={{ width: "100%" }} min={-90} max={90} step={0.000001} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name={["location", "longitude"]} label="Longitude">
                <InputNumber style={{ width: "100%" }} min={-180} max={180} step={0.000001} />
              </Form.Item>
            </Col>
          </Row>
        </VisibilityWrapper>
      </Form>

      <ButtonContainer>
        <Link to={routeNames.eventDetails}>
          <GhostButton>Cancel</GhostButton>
        </Link>
        <ButtonWrapper>
          <SubmitButton form={form} loading={false}>
            Save and Continue
          </SubmitButton>
        </ButtonWrapper>
      </ButtonContainer>
    </Container>
  );
};

export default EventType;
